package br.gov.go.pge.permanenciaweb.site.components

import androidx.compose.runtime.Composable
import br.gov.go.pge.permanenciaweb.shared.ResolvedRemovalDto
import br.gov.go.pge.permanenciaweb.site.IndustrialPalette
import br.gov.go.pge.permanenciaweb.site.pages.DashboardState
import br.gov.go.pge.permanenciaweb.site.pages.undoRemoval
import com.varabyte.kobweb.compose.css.ListStyle
import com.varabyte.kobweb.compose.css.VerticalAlign
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderBottom
import com.varabyte.kobweb.compose.ui.modifiers.borderLeft
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.flexDirection
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.listStyle
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.verticalAlign
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.forms.ButtonSize
import com.varabyte.kobweb.silk.components.icons.ChevronDownIcon
import com.varabyte.kobweb.silk.components.icons.ChevronUpIcon
import com.varabyte.kobweb.silk.style.ComponentKind
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.addVariantBase
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Aside
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Small
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

sealed interface RemovedListItemKind : ComponentKind

val RemovedListStyle = CssStyle.base {
    Modifier
        .listStyle(ListStyle.None)
        .margin(0.cssRem)
        .padding(0.cssRem)
}

val RemovedListItemStyle = CssStyle<RemovedListItemKind> {
    base {
        Modifier
            .padding(0.625.cssRem, 1.cssRem)
            // 1px hairline dividers stay in px per the units policy.
            .borderBottom(1.px, LineStyle.Solid, IndustrialPalette.Grid)
            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.Column)
            .gap(0.125.cssRem)
    }
}

val RemovedNoteStyle = CssStyle.base {
    Modifier.color(IndustrialPalette.Muted)
}

val ReenteredItemVariant = RemovedListItemStyle.addVariantBase {
    Modifier
        .borderLeft(0.1875.cssRem, LineStyle.Solid, IndustrialPalette.Orange)
        .backgroundColor(IndustrialPalette.OrangeTint)
}

@Composable
internal fun RemovedPanel(state: DashboardState, launchAction: ((suspend () -> Unit) -> Unit)) {
    val snapshot = state.snapshot
    val removed = snapshot?.removed ?: emptyList()
    val resolved = snapshot?.resolvedRemovals ?: emptyList()

    Aside(attrs = PanelStyle.toModifier().then(Modifier.id("removed")).toAttrs()) {
        PanelTitle { Text("Removidos hoje") }
        Ul(attrs = RemovedListStyle.toAttrs()) {
            for (r in removed) {
                Li(attrs = RemovedListItemStyle.toModifier().toAttrs()) {
                    Strong { Text(r.nome) }
                    Small(attrs = RemovedNoteStyle.toAttrs()) {
                        Text(
                            "entrou ${r.entrada} · removido ${r.removedAt} por ${r.removedBy}" +
                                (r.note?.let { " · $it" } ?: "")
                        )
                    }
                    if (state.me?.canManageRosterCorrections == true) {
                        Button(
                            onClick = { launchAction { state.undoRemoval(r.removalId) } },
                            variant = LinkButtonVariant,
                            size = ButtonSize.SM,
                        ) {
                            Text("desfazer")
                        }
                    }
                }
            }
        }
        if (snapshot != null && removed.isEmpty() && resolved.isEmpty()) {
            P(attrs = PanelEmptyStyle.toAttrs()) { Text("Ninguém foi removido manualmente hoje.") }
        }
        if (resolved.isNotEmpty()) {
            CollapsiblePanelTitle(
                expanded = state.historyExpanded,
                onToggle = { state.historyExpanded = !state.historyExpanded },
            ) {
                Text("Histórico de remoções — ${resolved.size}")
                if (state.historyExpanded) {
                    ChevronUpIcon(Modifier.margin(left = 0.375.cssRem).verticalAlign(VerticalAlign.Middle))
                } else {
                    ChevronDownIcon(Modifier.margin(left = 0.375.cssRem).verticalAlign(VerticalAlign.Middle))
                }
            }
            if (state.historyExpanded) {
                Ul(attrs = RemovedListStyle.toModifier().then(Modifier.id("resolved-list")).toAttrs()) {
                    for (r in resolved) {
                        Li(attrs = RemovedListItemStyle.toModifier(if (r.resolution == "REENTERED") ReenteredItemVariant else null).toAttrs()) {
                            Strong { Text(r.nome) }
                            Small(attrs = RemovedNoteStyle.toAttrs()) {
                                Text(
                                    "${resolutionText(r)} · removido ${r.removedAt} por ${r.removedBy}" +
                                        (r.note?.let { " · $it" } ?: "")
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

private fun resolutionText(r: ResolvedRemovalDto): String {
    val at = if (r.resolvedAt.length >= 16) r.resolvedAt.substring(11, 16) else r.resolvedAt
    return when (r.resolution) {
        "REENTERED" -> "⚠ voltou a entrar às $at"
        "UNDONE" -> "remoção desfeita às $at"
        "EXITED" -> "saiu do prédio às $at"
        else -> "resolvida às $at"
    }
}
